import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { getQueryFn } from "@/lib/queryClient";
import {
  PARTNER_RESTAURANTS_URL,
  buildPartnerMembershipQueryKey,
} from "@/lib/partnerMembershipQueryKey";

export { PARTNER_RESTAURANTS_URL, buildPartnerMembershipQueryKey };

/**
 * Task #2623 — partner-portal access fallback.
 *
 * A user can reach the partner portal in two ways:
 *   1. They hold the Eatzy-wide `RestaurantManager` role (checked by
 *      `usePermissions`), or
 *   2. They have at least one active `restaurant_partners` membership —
 *      which is exactly what accepting an invite grants. Freshly invited
 *      staff/managers/owners have no Eatzy role yet, only the local
 *      membership row, so the Eatzy-role check alone would (wrongly) deny
 *      them with "Access Denied" right after they accept.
 *
 * This hook reports whether the authenticated user has any active partner
 * membership so `RequirePermission` can grant partner-portal access when the
 * Eatzy role is absent. The server still authoritatively enforces access on
 * every partner endpoint via `requirePartner`; this only governs which
 * client route the user is allowed to render.
 */
export function usePartnerMembership(enabled: boolean): {
  hasMembership: boolean;
  isLoading: boolean;
} {
  const { isAuthenticated, token, user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery<unknown[]>({
    // Scope the cache by the authenticated user id (mirrors usePermissions)
    // so one user's membership array can never satisfy a different user's
    // gate after a sign-out/sign-in switch in the same tab.
    queryKey: buildPartnerMembershipQueryKey(userId),
    queryFn: getQueryFn<unknown[]>({
      on401: "throw",
      url: PARTNER_RESTAURANTS_URL,
    }),
    enabled: enabled && isAuthenticated && !!token && !!userId,
    // staleTime: 0 so the membership is re-fetched on every component mount
    // and every window-focus event (TanStack Query's refetchOnWindowFocus
    // default is true). This means a revocation is detected the moment the
    // user navigates to a partner route or returns to the tab — not after a
    // fixed 60-second grace window.
    staleTime: 0,
    // Polling backstop: catch revocations for users who stay on one page
    // without navigating or switching tabs. 30 s is fast enough to feel
    // prompt while being cheap (one lightweight GET every half-minute).
    refetchInterval: 30 * 1000,
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | null)?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });

  return {
    hasMembership: Array.isArray(query.data) && query.data.length > 0,
    // Only surface "loading" while a real fetch is needed. When disabled the
    // query never runs, so it must not report loading (that would wedge the
    // gate spinner on routes that don't opt into the membership fallback).
    isLoading: enabled && query.isPending && query.fetchStatus !== "idle",
  };
}
